import * as lib from '~/dependencies';
import { colors } from '~/layout';

export const Container = lib.styled.div`
  display: flex;
  flex-direction: column;
`;

export const ContentWrapper = lib.styled.div`
  display: flex;
  flex: 1;
  flex-direction: row;
  gap: 10px;

  .icon-file, .icon-download { 
    svg {
      width: 20px;
    }
  }
  
  .icon-download {
    margin-top: 2px;
  }

  /* Hide the fullscreen button (WebKit browsers only) */
  video::-webkit-media-controls-fullscreen-button {
    display: none !important;
  }

  /* Hide the volume control (WebKit browsers only) */
  video::-webkit-media-controls-volume-slider {
    display: none !important;
  }

  /* Hide the "more" (⋮) button / overflow menu (WebKit browsers only) */
  video::-webkit-media-controls-overflow-button {
    display: none !important;
  }
`;

export const FileInfoContainer = lib.styled.div`
  display: flex;
  flex-direction: column;
  flex: 2;
  align-items: flex-start;
  margin-left: 5px;
`;

export const FileName = lib.styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 23ch;
  font-weight: 600;
  font-size: 14px;
  color: #4c595f;
`;

export const FileDetails = lib.styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 30ch;
  font-size: 10px;
  color: #555;
`;

export const Image = lib.styled.img<{ width?: string }>`
  width: 100%;
  height: 165px;
  object-fit: cover;
  border-radius: 8px;
  margin-bottom: 15px;
  cursor: zoom-in;
  background: ${colors.lightGray18};
`;

export const MediaErrorPlaceholder = lib.styled.div`
  width: 100%;
  height: 70px;
  border-radius: 8px;
  margin-bottom: 15px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 5px;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  color: ${colors.darkGray13 ?? '#333'};
  background: ${colors.lightGray18 ?? '#f5f5f5'};

  .icon-file {
    display: flex;
    opacity: 0.7;

    svg {
      width: 26px;
      height: 26px;

      path {
        fill: #3D4F59;
      }
    }
  }

  span {
    font-weight: 700;
  }
`;

export const VideoThumb = lib.styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
  display: block;
  background: #000;
`;

export const VideoPlayOverlay = lib.styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.2);
  transition: background 180ms ease, transform 180ms ease;
  pointer-events: none;
  border-radius: 8px;

  svg {
    width: 56px;
    height: 56px;
    color: #fff;
    filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.55));
    transition: transform 180ms ease;
  }
`;

export const VideoDurationBadge = lib.styled.span`
  position: absolute;
  left: 8px;
  bottom: 8px;
  padding: 2px 6px;
  background: rgba(0, 0, 0, 0.65);
  color: #fff;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.3px;
  font-variant-numeric: tabular-nums;
  pointer-events: none;
  z-index: 1;
`;

export const VideoThumbWrapper = lib.styled.div`
  position: relative;
  display: inline-block;
  width: 270px;
  height: 165px;
  margin-bottom: 10px;
  cursor: pointer;
  border-radius: 8px;
  overflow: hidden;

  &:hover ${VideoPlayOverlay} {
    background: rgba(0, 0, 0, 0.35);
  }

  &:hover ${VideoPlayOverlay} svg {
    transform: scale(1.08);
  }
`;

export const VideoPreviewFull = lib.styled.video`
  max-width: 85vw;
  max-height: 80vh;
  border-radius: 8px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);
  background: #000;
  outline: none;
  cursor: default;
`;

export const PdfPreviewCard = lib.styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  padding: 8px;
  width: 270px;
  max-width: 100%;
  border-radius: 8px;
  cursor: pointer;
  background: ${colors.white ?? '#fff'};
  border: 1px solid ${colors.lightGray18 ?? '#e5e5e5'};
  box-sizing: border-box;
  transition: background 150ms ease, box-shadow 150ms ease;

  &:hover {
    background: ${colors.lightGray18 ?? '#f5f5f5'};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }
`;

export const PdfPreviewIcon = lib.styled.div`
  display: flex;
  flex-shrink: 0;

  svg {
    width: 32px;
    height: 32px;
    color: #d32f2f;
  }
`;

export const PdfPreviewInfo = lib.styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  gap: 2px;
`;

export const PdfPreviewName = lib.styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 600;
  font-size: 14px;
  color: ${colors.darkGray13 ?? '#333'};
`;

export const PdfPreviewHint = lib.styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: ${colors.lightBlue05 ?? '#0a6ec0'};
  font-weight: 500;

  svg {
    width: 14px;
    height: 14px;
  }
`;

export const PdfPreviewActions = lib.styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
`;

export const PdfPreviewFrame = lib.styled.iframe`
  width: 85vw;
  height: 85vh;
  max-width: 100%;
  max-height: 100%;
  border: none;
  border-radius: 8px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);
  background: #fff;
  cursor: default;
`;

export const ImagePreviewBackdrop = lib.styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.85);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: zoom-out;
  padding: 80px 20px 20px;
  box-sizing: border-box;
`;

export const ImagePreviewFull = lib.styled.img`
  max-width: 85vw;
  max-height: 80vh;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);
  cursor: default;
`;

export const ImagePreviewActions = lib.styled.div`
  position: absolute;
  top: 20px;
  right: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
  z-index: 2;
`;

export const ImagePreviewIconButton = lib.styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  border: none;
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 150ms ease;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }

  svg {
    width: 22px;
    height: 22px;
  }
`;

export const ImagePreviewMenu = lib.styled.div`
  position: absolute;
  top: 70px;
  right: 20px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
  min-width: 180px;
  overflow: hidden;
  z-index: 2;
`;

export const ImagePreviewMenuItem = lib.styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 12px 16px;
  background: transparent;
  border: none;
  cursor: pointer;
  font-family: inherit;
  font-size: 14px;
  color: ${colors.darkGray13 ?? '#333'};
  text-align: left;
  transition: background 150ms ease;

  &:hover {
    background: ${colors.lightGray18 ?? '#f5f5f5'};
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

export const Divider = lib.styled.div`
  width: 100%;
  height: 0.5px;
  background-color: ${colors.dark04};
  margin: -5px 0 10px 0;
`;
